import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import AppLayout from '@/components/layout/AppLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  QrCode, ChevronDown, Check, Video, Bell, Clock,
  Heart, FileText, Thermometer, X, AlertTriangle,
  Frown, Annoyed, Meh, Smile, Laugh,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  profiloPaziente, profiliGestiti, prossimaVisita, notifiche,
} from '@/data/mockPatientData';

/* ══════ FACCINE SODDISFAZIONE (5 livelli, flat SVG) ══════ */
const faccine = [
  { key: 'molto-insoddisfatto', label: 'Molto insoddisfatto', color: '#DC2626', bgColor: '#FEE2E2' },
  { key: 'insoddisfatto', label: 'Insoddisfatto', color: '#EA580C', bgColor: '#FFF7ED' },
  { key: 'neutro', label: 'Neutro', color: '#CA8A04', bgColor: '#FEFCE8' },
  { key: 'soddisfatto', label: 'Soddisfatto', color: '#65A30D', bgColor: '#F7FEE7' },
  { key: 'molto-soddisfatto', label: 'Molto soddisfatto', color: '#15803D', bgColor: '#F0FDF4' },
];

const FACE_ICONS = {
  'molto-insoddisfatto': Frown,
  'insoddisfatto': Annoyed,
  'neutro': Meh,
  'soddisfatto': Smile,
  'molto-soddisfatto': Laugh,
};

function FaceIcon({ type, size = 48 }) {
  const face = faccine.find(f => f.key === type);
  const c = face?.color || '#6B7280';
  const Icon = FACE_ICONS[type] || Meh;
  return <Icon width={size} height={size} color={c} strokeWidth={1.8} />;
}

/* ══════ QR CODE MODAL ══════ */
function QrModal({ open, onClose }) {
  if (!open) return null;
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-foreground/30 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="qr-modal-overlay"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        className="bg-card rounded-3xl shadow-2xl max-w-sm w-full overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="qr-modal-panel"
      >
        <div className="p-8 flex flex-col items-center text-center">
          <div className="w-48 h-48 rounded-3xl bg-gradient-to-br from-sky-50 to-teal-50 border-2 border-sky-100 flex items-center justify-center mb-6" data-testid="qr-code-display">
            <QrCode className="w-28 h-28 text-sky-600" strokeWidth={1.5} />
          </div>
          <h2 className="text-xl font-bold text-foreground">QR Code Visita</h2>
          <p className="text-sm text-muted-foreground mt-2 max-w-xs leading-relaxed">
            Mostra questo codice all'operatore sanitario all'inizio di ogni visita domiciliare.
          </p>
          <div className="mt-4 px-5 py-2 rounded-full bg-sky-50 border border-sky-100">
            <span className="text-xs font-bold text-sky-700 font-mono tracking-wider">{profiloPaziente.qrCode}</span>
          </div>
          <p className="text-[10px] text-muted-foreground mt-3">Paziente: {profiloPaziente.nome} {profiloPaziente.cognome}</p>
        </div>
        <div className="px-6 pb-6">
          <Button
            className="w-full h-12 rounded-2xl gap-2 text-base"
            onClick={onClose}
            data-testid="qr-modal-close"
          >
            <Check className="w-5 h-5" />Chiudi
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
}

/* ══════ SWITCH PROFILO ══════ */
function ProfileSwitcher({ activeProfilo, onSwitch }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="relative" data-testid="profile-switcher">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 px-3 py-2 rounded-2xl hover:bg-muted/40 transition-colors"
        data-testid="profile-switcher-trigger"
      >
        <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-sky-400 to-teal-400 flex items-center justify-center text-white text-sm font-bold shadow-sm">
          {activeProfilo.avatar}
        </div>
        <div className="text-left">
          <p className="text-base font-bold text-foreground leading-tight">Buongiorno, {activeProfilo.nome.split(' ')[0]}</p>
          <p className="text-[11px] text-muted-foreground">{activeProfilo.relazione}</p>
        </div>
        <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ml-1 ${open ? 'rotate-180' : ''}`} />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }}
            className="absolute left-0 top-full mt-2 w-64 bg-card border border-border/40 rounded-2xl shadow-lg z-50 overflow-hidden"
            data-testid="profile-dropdown"
          >
            <div className="p-2">
              <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider px-3 py-2">Profili gestiti</p>
              {profiliGestiti.map((p) => (
                <button
                  key={p.id}
                  className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl text-left transition-colors ${p.attivo ? 'bg-sky-50 border border-sky-100' : 'hover:bg-muted/30'}`}
                  onClick={() => { onSwitch(p); setOpen(false); }}
                  data-testid={`profile-option-${p.id}`}
                >
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center text-xs font-bold text-white ${p.attivo ? 'bg-gradient-to-br from-sky-400 to-teal-400' : 'bg-slate-300'}`}>
                    {p.avatar}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-foreground">{p.nome}</p>
                    <p className="text-[10px] text-muted-foreground">{p.relazione}</p>
                  </div>
                  {p.attivo && <Check className="w-4 h-4 text-sky-500" />}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ══════ NOTIFICA ICON ══════ */
const notifIcons = {
  promemoria: { icon: Thermometer, color: '#F59E0B', bg: '#FFFBEB' },
  documento: { icon: FileText, color: '#3B82F6', bg: '#EFF6FF' },
  aggiornamento: { icon: Heart, color: '#10B981', bg: '#ECFDF5' },
};

/* ══════ PAGINA DASHBOARD ══════ */
export default function PatientDashboard() {
  const [showQr, setShowQr] = useState(false);
  const [activeProfilo, setActiveProfilo] = useState(profiliGestiti[0]);
  const [feedbackMode, setFeedbackMode] = useState(false);
  const [feedbackDone, setFeedbackDone] = useState(false);
  const [selectedFace, setSelectedFace] = useState(null);

  const visita = prossimaVisita;

  return (
    <AppLayout role="paziente">
      <div className="space-y-6 max-w-3xl mx-auto" data-testid="patient-dashboard">

        {/* ═══ HEADER ═══ */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <ProfileSwitcher activeProfilo={activeProfilo} onSwitch={setActiveProfilo} />
          <Button
            className="h-12 px-5 rounded-2xl gap-2.5 bg-gradient-to-r from-sky-500 to-teal-500 hover:from-sky-600 hover:to-teal-600 text-white shadow-md text-sm font-bold"
            onClick={() => setShowQr(true)}
            data-testid="btn-mostra-qr"
          >
            <QrCode className="w-5 h-5" />Mostra QR Visita
          </Button>
        </div>

        {/* ═══ WIDGET A: Prossima Visita ═══ */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
          <Card className="border-0 shadow-[0_2px_12px_rgba(14,165,233,0.1)] rounded-3xl overflow-hidden bg-gradient-to-br from-sky-50 via-white to-teal-50 dark:from-sky-950/30 dark:via-card dark:to-teal-950/20" data-testid="widget-prossima-visita">
            <CardContent className="p-0">
              <div className="px-6 pt-5 pb-2">
                <p className="text-[11px] font-bold text-sky-500 uppercase tracking-wider">Prossima visita</p>
              </div>
              <div className="px-6 pb-6 flex items-center gap-5">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-sky-400 to-teal-400 flex items-center justify-center text-white text-lg font-bold shadow-md flex-shrink-0">
                  {visita.avatarOperatore}
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="text-xl font-bold text-foreground leading-tight">{visita.tipo}</h2>
                  <p className="text-sm text-muted-foreground mt-0.5">{visita.operatore}</p>
                  <p className="text-xs text-muted-foreground mt-1.5">{visita.note}</p>
                </div>
                <div className="flex flex-col items-end gap-2 flex-shrink-0">
                  <div className="text-right">
                    <p className="text-2xl font-extrabold text-sky-600">{visita.ora}</p>
                    <p className="text-sm font-semibold text-foreground">{visita.data}</p>
                  </div>
                  <Badge className="rounded-full px-3 py-1 border-0 bg-emerald-100 text-emerald-700 text-xs font-bold" data-testid="badge-stato-visita">
                    <div className="w-2 h-2 rounded-full bg-emerald-500 mr-1.5 animate-pulse" />
                    In arrivo
                  </Badge>
                </div>
              </div>

              {/* Feedback rapido */}
              <AnimatePresence mode="wait">
                {!feedbackMode && !feedbackDone && (
                  <motion.div key="cta" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="px-6 pb-5">
                    <button
                      onClick={() => setFeedbackMode(true)}
                      className="w-full py-3 rounded-2xl border border-sky-200 bg-sky-50/50 text-sm font-semibold text-sky-700 hover:bg-sky-100 transition-colors flex items-center justify-center gap-2"
                      data-testid="btn-lascia-feedback"
                    >
                      <span className="text-lg">{'\uD83D\uDCAC'}</span>La visita e terminata? Lascia un feedback
                    </button>
                  </motion.div>
                )}
                {feedbackMode && !feedbackDone && (
                  <motion.div key="rating" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="px-6 pb-5 space-y-3" data-testid="dashboard-feedback-rating">
                    <p className="text-sm font-bold text-foreground text-center">Come valuti la visita di oggi?</p>
                    <div className="flex justify-center gap-2">
                      {faccine.map((f) => (
                        <button
                          key={f.key}
                          onClick={() => {
                            setSelectedFace(f.key);
                            setFeedbackDone(true);
                            setFeedbackMode(false);
                          }}
                          className="flex flex-col items-center gap-1 px-2.5 py-2.5 rounded-2xl border-2 border-transparent transition-all active:scale-95 hover:scale-105"
                          style={{ backgroundColor: f.bgColor }}
                          data-testid={`dashboard-rating-${f.key}`}
                        >
                          <FaceIcon type={f.key} size={40} />
                          <span className="text-[9px] font-bold leading-tight text-center max-w-[60px]" style={{ color: f.color }}>{f.label}</span>
                        </button>
                      ))}
                    </div>
                    <button onClick={() => setFeedbackMode(false)} className="w-full text-center text-xs text-muted-foreground hover:text-foreground transition-colors py-1" data-testid="btn-annulla-feedback">
                      Annulla
                    </button>
                  </motion.div>
                )}
                {feedbackDone && selectedFace && (
                  <motion.div
                    key="readonly"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="px-6 pb-5 flex items-center justify-center"
                    data-testid="dashboard-feedback-done"
                  >
                    {(() => {
                      const f = faccine.find(x => x.key === selectedFace);
                      return (
                        <div
                          className="flex flex-col items-center gap-1.5 px-5 py-3 rounded-2xl ring-2"
                          style={{ backgroundColor: f?.bgColor, '--tw-ring-color': f?.color }}
                        >
                          <FaceIcon type={selectedFace} size={56} />
                          <span className="text-xs font-bold" style={{ color: f?.color }}>{f?.label}</span>
                        </div>
                      );
                    })()}
                  </motion.div>
                )}
              </AnimatePresence>
            </CardContent>
          </Card>
        </motion.div>

        {/* ═══ WIDGET B: Notifiche e Promemoria ═══ */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <div className="space-y-3" data-testid="widget-notifiche">
            <div className="flex items-center gap-2 px-1">
              <Bell className="w-4 h-4 text-muted-foreground" />
              <h3 className="text-sm font-bold text-foreground">Notifiche e Promemoria</h3>
              {notifiche.filter(n => !n.letta).length > 0 && (
                <Badge className="rounded-full h-5 px-2 border-0 bg-red-500 text-white text-[10px] font-bold">
                  {notifiche.filter(n => !n.letta).length}
                </Badge>
              )}
            </div>
            <div className="space-y-2">
              {notifiche.map((n, i) => {
                const cfg = notifIcons[n.tipo] || notifIcons.promemoria;
                const Icon = cfg.icon;
                return (
                  <motion.div key={n.id} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.12 + i * 0.05 }}>
                    <Card
                      className={`border-0 shadow-[0_1px_4px_rgba(0,0,0,0.05)] rounded-2xl transition-colors ${!n.letta ? 'bg-card ring-1 ring-sky-100' : 'bg-card/60'}`}
                      data-testid={`notifica-${n.id}`}
                    >
                      <CardContent className="p-4 flex items-center gap-4">
                        <div
                          className="w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: cfg.bg }}
                        >
                          <Icon className="w-5 h-5" style={{ color: cfg.color }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-semibold leading-tight ${!n.letta ? 'text-foreground' : 'text-muted-foreground'}`}>{n.titolo}</p>
                          <p className="text-[11px] text-muted-foreground mt-0.5 leading-relaxed">{n.descrizione}</p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className="text-[10px] text-muted-foreground">{n.ora}</span>
                          {!n.letta && <div className="w-2.5 h-2.5 rounded-full bg-sky-500" />}
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </motion.div>

        {/* ═══ WIDGET C: Azioni Rapide ═══ */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
          <div className="grid grid-cols-2 gap-3" data-testid="widget-azioni-rapide">
            <button
              className="flex flex-col items-center justify-center gap-3 p-6 rounded-3xl bg-gradient-to-br from-blue-500 to-indigo-600 text-white shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all active:scale-[0.98]"
              onClick={() => toast.success('Teleconsulto in avvio...', { description: 'Connessione con il medico in corso.' })}
              data-testid="btn-avvia-teleconsulto"
            >
              <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center backdrop-blur-sm">
                <Video className="w-7 h-7 text-white" />
              </div>
              <span className="text-base font-bold">Avvia Teleconsulto</span>
              <span className="text-[11px] text-white/70">Videochiamata con il medico</span>
            </button>

            <button
              className="flex flex-col items-center justify-center gap-3 p-6 rounded-3xl bg-gradient-to-br from-orange-400 to-red-500 text-white shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all active:scale-[0.98]"
              onClick={() => toast('Segnalazione inviata alla COT', { description: 'Un operatore ti contattara il prima possibile.', icon: <AlertTriangle className="w-5 h-5 text-orange-500" /> })}
              data-testid="btn-richiedi-aiuto"
            >
              <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center backdrop-blur-sm">
                <Bell className="w-7 h-7 text-white" />
              </div>
              <span className="text-base font-bold">Richiedi Aiuto</span>
              <span className="text-[11px] text-white/70">Segnala alla Centrale Operativa</span>
            </button>
          </div>
        </motion.div>

        {/* ═══ Info PAI attivo ═══ */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}>
          <Card className="border-0 shadow-[0_1px_4px_rgba(0,0,0,0.05)] rounded-2xl" data-testid="widget-info-pai">
            <CardContent className="p-5 flex items-center gap-4">
              <div className="w-11 h-11 rounded-2xl bg-emerald-50 flex items-center justify-center flex-shrink-0">
                <Heart className="w-5 h-5 text-emerald-500" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-foreground">Il tuo Piano Assistenziale e attivo</p>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  Codice: {profiloPaziente.codicePAI} — Attivo dal {profiloPaziente.dataAttivazione} — Provider: {profiloPaziente.provider}
                </p>
              </div>
              <Badge className="rounded-full px-3 py-1 border-0 bg-emerald-50 text-emerald-600 text-[10px] font-bold flex-shrink-0">
                Attivo
              </Badge>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* QR Modal */}
      <AnimatePresence>
        {showQr && <QrModal open={showQr} onClose={() => setShowQr(false)} />}
      </AnimatePresence>
    </AppLayout>
  );
}
